import React from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./simulazioneRendimentiAnnoSolareCompare.module.scss";

const SimulazioneRendimentiAnnoSolareCompare = ({ data, data2 }) => {
  const { t } = useTranslation();

  const formatValue = (value) => {
    if (value === null || value === undefined) return "0";
    return `${Number(value).toFixed(1)}`;
  };

  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Proxima Nova",
  };

  const renderCustomBarLabel = (props) => {
    const { x, y, width, value } = props;
    if (value === null || value === undefined) return null;
    const formattedValue = Math.round(value);
    const isPositive = Number(value) >= 0;
    const yPos = isPositive ? y - 10 : y + 20;
    return (
      <text
        x={x + width / 2}
        y={yPos}
        fill={props.fill}
        textAnchor="middle"
        fontSize={12}
        fontFamily="Proxima Nova"
      >
        {`${formattedValue}%`}
      </text>
    );
  };

  // Get data from both portfolios
  const simulazioneRendimentiData1 =
    data.analisi_backtest.simulazione_rendimenti_portafoglio_anno_solare;
  const simulazioneRendimentiData2 =
    data2?.analisi_backtest?.simulazione_rendimenti_portafoglio_anno_solare ||
    [];

  // Create a map to merge data by date
  const dataMap = new Map();

  // Process portfolio 1
  simulazioneRendimentiData1.forEach((item) => {
    const year = new Date(item.date).getFullYear().toString();
    dataMap.set(year, {
      date: item.date,
      value1: Math.round(item.value),
      value2: null,
    });
  });

  // Process portfolio 2
  simulazioneRendimentiData2.forEach((item) => {
    const year = new Date(item.date).getFullYear().toString();
    if (dataMap.has(year)) {
      // Update existing entry
      dataMap.get(year).value2 = Math.round(item.value);
    } else {
      // Create new entry
      dataMap.set(year, {
        date: item.date,
        value1: null,
        value2: Math.round(item.value),
      });
    }
  });

  // Convert map to array and sort by date
  const formattedData = Array.from(dataMap.values()).sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  if (formattedData.length === 0) {
    return null;
  }
  // Custom tooltip component with TypeScript interface
  interface TooltipProps {
    active?: boolean;
    payload?: any[];
    label?: string;
  }

  const CustomTooltip: React.FC<TooltipProps> = ({
    active,
    payload,
    label,
  }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div
        style={{
          backgroundColor: colorsConfig.finecoWhite || "#fff",
          padding: "10px",
          border: `1px solid ${colorsConfig.FinecoGrayLight1 || "#ccc"}`,
          borderRadius: "4px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <p
          style={{
            marginBottom: "5px",
            fontWeight: "bold",
            fontFamily: "Proxima Nova",
            fontSize: "12px",
            color: colorsConfig.finecoBlack || "#000",
          }}
        >
          {`Anno: ${new Date(label).getFullYear()}`}
        </p>
        {payload.map((entry, index) => {
          if (entry.value === null || entry.value === undefined) return null;
          return (
            <p
              key={`tooltip-${index}`}
              style={{
                margin: "3px 0",
                fontFamily: "Proxima Nova",
                fontSize: "12px",
                color: entry.color,
              }}
            >
              {`${entry.name}: ${Number(entry.value).toFixed(1)}%`}
            </p>
          );
        })}
      </div>
    );
  };

  const values = formattedData.flatMap((d) =>
    [d.value1, d.value2].filter((v) => v !== null && v !== undefined),
  );
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);

  // Add clean 15% padding and round to nearest 5
  const paddedMin = Math.floor((rawMin - Math.abs(rawMin) * 0.5) / 5) * 5;
  const paddedMax = Math.ceil((rawMax + Math.abs(rawMax) * 0.5) / 5) * 5;

  // Generate ticks every 5 units
  const ticks = [];
  for (let i = paddedMin; i <= paddedMax; i += 5) {
    ticks.push(i);
  }
  const CustomizedLegend = (props: any) => {
    if (!data2) return null;
    const legendItems = [
      {
        value: t("portafoglioCompare.portafoglio1"),
        color: colorsConfig.portfolio1,
      },
      {
        value: t("portafoglioCompare.portafoglio2"),
        color: colorsConfig.portfolio2,
      },
    ];

    return (
      <div className={styles.legendContainer}>
        {legendItems.map((entry, index) => (
          <div key={`item-${index}`} className={styles.legendItem}>
            <div
              className={styles.legendDot}
              style={{ backgroundColor: entry.color }}
            />
            <span className={styles.legendCopy}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.sectionTitle}>
        {t("portfolio.simulazioneRendimentiAnnoSolareCompare")}
      </div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={260} debounce={500}>
          <BarChart
            data={formattedData}
            margin={{ top: 20, right: 10, left: -15, bottom: 10 }}
            barGap={2} // Gap between bars in the same category (between portfolio 1 and 2)
            barCategoryGap={24}
          >
            <CartesianGrid
              strokeWidth={1}
              stroke={colorsConfig.GrayLight2}
              strokeOpacity={1}
              horizontal={true}
              vertical={false}
            />
            {/* Reference line at 0% */}
            <ReferenceLine
              y={0}
              stroke={colorsConfig.finecoBlack}
              strokeWidth={1}
              strokeOpacity={0.8}
            />
            <XAxis
              dataKey="date"
              tick={chartCopy}
              tickLine={false}
              tickFormatter={(value) =>
                new Date(value).toLocaleDateString("it-IT", { year: "numeric" })
              }
              dy={10}
              strokeDasharray="none"
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <YAxis
              tickFormatter={formatValue}
              tickLine={false}
              tick={chartCopy}
              domain={[paddedMin, paddedMax]}
              ticks={ticks} // Force all ticks including those from the padding
              interval={0} // Force all ticks to render
              allowDataOverflow={false}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />
            <Legend content={<CustomizedLegend />} />
            <Bar
              dataKey="value1"
              name="Portafoglio 1"
              fill={colorsConfig.portfolio1}
              barSize={20}
              radius={[2, 2, 0, 0]}
              label={(props) =>
                renderCustomBarLabel({
                  ...props,
                  fill: colorsConfig.FinecoBlack,
                })
              }
              isAnimationActive={false}
            />
            <Bar
              dataKey="value2"
              name="Portafoglio 2"
              fill={colorsConfig.portfolio2}
              barSize={20}
              radius={[4, 4, 0, 0]}
              label={(props) =>
                renderCustomBarLabel({
                  ...props,
                  fill: colorsConfig.FinecoBlack,
                })
              }
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SimulazioneRendimentiAnnoSolareCompare;
